import React, { useState, useEffect } from 'react';
import { AlertCircle, AlertTriangle, User, Building2, Mail, MessageSquare, Clock, ChevronRight, GitMerge, Shield, X } from 'lucide-react';
import C from '../../data/colors';
import axios from 'axios';
import AdminPageHeader from './AdminPageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

import { theme } from '../../data/colors';

const severityConfig = {
  red: { bg: theme.statusErrorTint, text: theme.statusError, icon: AlertCircle, label: 'Critical' },
  yellow: { bg: theme.statusWarningTint, text: theme.statusWarning, icon: AlertTriangle, label: 'Warning' },
};

const typeIcons = {
  Guest: User,
  Vendor: Building2,
  Email: Mail,
  SMS: MessageSquare,
  System: Shield,
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const AttentionTab = ({ onNavigate, onResolveDuplicate }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, red, yellow

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/attention-items`);
      setItems(res.data.items || []);
    } catch (err) {
      console.error('Error fetching attention items:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = filter === 'all' 
    ? items 
    : items.filter(i => i.severity === filter);

  const handleItemClick = (item) => {
    // Parse deep link and navigate
    const params = new URLSearchParams(item.deep_link.split('?')[1] || '');
    const tab = params.get('tab');
    const entityId = params.get('vendorId') || params.get('guestId') || params.get('logId');
    const section = params.get('section');
    const action = params.get('action');
    const relatedId = params.get('relatedId');
    const highlight = params.get('highlight');
    
    if (onNavigate) {
      onNavigate(tab, entityId, section, { action, relatedId, highlight });
    }
  };

  const handleResolveDuplicate = (item) => {
    if (onResolveDuplicate) {
      onResolveDuplicate(item.entity_id, item.related_entity_id);
    }
  };

  const handleDismissItem = async (item, e) => {
    e.stopPropagation();
    if (!window.confirm('Dismiss this attention item? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API_URL}/api/attention-items/${item.id}`);
      fetchItems();
    } catch (err) {
      console.error('Error dismissing item:', err);
    }
  };

  const redCount = items.filter(i => i.severity === 'red').length;
  const yellowCount = items.filter(i => i.severity === 'yellow').length;

  return (
    <div data-testid="attention-tab" style={{ padding: '0 24px' }}>
      <AdminPageHeader
        eyebrow="Alerts"
        title="Attention"
        tagline="Data-integrity issues and duplicates that need your review."
      />

      {/* Summary Header */}
      <div style={{
        display: 'flex',
        gap: 16,
        marginBottom: 20,
        flexWrap: 'wrap',
      }}>
        <FilterBadge 
          label="All Items" 
          count={items.length} 
          active={filter === 'all'} 
          onClick={() => setFilter('all')} 
        />
        <FilterBadge 
          label="Critical" 
          count={redCount} 
          color={theme.statusError}
          active={filter === 'red'} 
          onClick={() => setFilter('red')} 
        />
        <FilterBadge 
          label="Warning" 
          count={yellowCount} 
          color={theme.statusWarning}
          active={filter === 'yellow'} 
          onClick={() => setFilter('yellow')} 
        />
      </div>

      {/* Items Table */}
      <div style={{ 
        background: C.bgCard, 
        borderRadius: 12, 
        border: `1px solid ${C.border}`,
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: C.bgPrimary, borderBottom: `1px solid ${C.border}` }}>
              <th style={thStyle}>Severity</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Issue</th>
              <th style={thStyle}>Entity</th>
              <th style={thStyle}>Missing Fields</th>
              <th style={thStyle}>Reason</th>
              <th style={thStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: C.textSecondary }}>Loading...</td></tr>
            ) : filteredItems.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: C.textSecondary }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <AlertCircle size={32} color={theme.statusSuccess} />
                    <span style={{ fontWeight: 500, color: C.textPrimary }}>All clear! No attention items.</span>
                  </div>
                </td>
              </tr>
            ) : (
              filteredItems.map((item) => {
                const SeverityIcon = severityConfig[item.severity]?.icon || AlertCircle;
                const TypeIcon = typeIcons[item.type] || User;
                const sevConfig = severityConfig[item.severity] || severityConfig.yellow;
                const isDuplicate = item.reason_code === 'DUPLICATE_GUEST_DETECTED';
                const isSystemBlock = item.reason_code?.includes('BLOCKED') || item.reason_code?.includes('INTEGRITY');
                
                return (
                  <tr 
                    key={item.id}
                    data-testid={`attention-item-${item.id}`}
                    onClick={() => handleItemClick(item)}
                    style={{ 
                      borderBottom: `1px solid ${C.border}`, 
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = C.bgCardHover}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={tdStyle}>
                      <div style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: 6,
                        padding: '4px 10px',
                        borderRadius: 12,
                        background: sevConfig.bg,
                        color: sevConfig.text,
                        fontSize: 11,
                        fontWeight: 600,
                      }}>
                        <SeverityIcon size={12} />
                        {sevConfig.label}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <TypeIcon size={14} color={C.textSecondary} />
                        <span style={{ fontSize: 12, color: C.textSecondary }}>{item.type}</span>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 500, color: C.textPrimary }}>{item.title}</div>
                      {item.extra?.error && (
                        <div style={{ fontSize: 11, color: theme.statusError, marginTop: 2 }}>
                          {item.extra.error.substring(0, 50)}...
                        </div>
                      )}
                      {/* Show matching/differing fields for duplicates */}
                      {isDuplicate && item.matching_fields?.length > 0 && (
                        <div style={{ fontSize: 10, color: theme.statusSuccess, marginTop: 2 }}>
                          Match: {item.matching_fields.join(', ')}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 13, color: C.textPrimary }}>{item.entity_name}</span>
                    </td>
                    <td style={tdStyle}>
                      {item.missing_fields?.length > 0 ? (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {item.missing_fields.map(field => (
                            <span 
                              key={field}
                              style={{ 
                                fontSize: 10, 
                                padding: '2px 6px', 
                                background: theme.statusWarningTint, 
                                color: theme.statusWarning,
                                borderRadius: 4,
                                fontWeight: 500,
                                textTransform: 'capitalize',
                              }}
                            >
                              {field}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ fontSize: 11, color: C.textSecondary }}>-</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <span style={{ 
                        fontSize: 10, 
                        fontFamily: 'monospace', 
                        background: isSystemBlock ? theme.statusErrorTint : C.bgPrimary, 
                        padding: '2px 6px', 
                        borderRadius: 4,
                        color: isSystemBlock ? theme.statusError : C.textSecondary,
                      }}>
                        {item.reason_code}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {isDuplicate ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleResolveDuplicate(item); }}
                            data-testid={`resolve-duplicate-${item.id}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '6px 12px',
                              background: theme.statusSuccess,
                              color: theme.textInverse,
                              border: 'none',
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 500,
                              cursor: 'pointer',
                            }}
                          >
                            <GitMerge size={14} /> Resolve
                          </button>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleItemClick(item); }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '6px 12px',
                              background: C.gold,
                              color: theme.textPrimary,
                              border: 'none',
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 500,
                              cursor: 'pointer',
                            }}
                          >
                            View <ChevronRight size={14} />
                          </button>
                        )}
                        <button
                          onClick={(e) => handleDismissItem(item, e)}
                          title="Dismiss"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '6px',
                            background: 'transparent',
                            color: C.textSecondary,
                            border: `1px solid ${C.border}`,
                            borderRadius: 6,
                            cursor: 'pointer',
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const thStyle = { 
  padding: '12px 16px', 
  textAlign: 'left', 
  fontSize: 11, 
  fontWeight: 600, 
  color: C.textSecondary, 
  textTransform: 'uppercase' 
};

const tdStyle = { 
  padding: '12px 16px', 
  fontSize: 13, 
  color: C.textPrimary 
};

const FilterBadge = ({ label, count, color, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 16px',
      background: active ? (color ? `${color}20` : C.bgCardHover) : C.bgCard,
      border: `1px solid ${active ? (color || C.gold) : C.border}`,
      borderRadius: 8,
      cursor: 'pointer',
      transition: 'all 0.15s',
    }}
  >
    <span style={{ fontSize: 13, color: color || C.textPrimary, fontWeight: active ? 600 : 400 }}>{label}</span>
    <span style={{ 
      background: color || C.gold, 
      color: color ? theme.textInverse : theme.textPrimary, 
      fontSize: 11, 
      fontWeight: 600,
      padding: '2px 8px',
      borderRadius: 10,
    }}>
      {count}
    </span>
  </button>
);

export default AttentionTab;
